import React, { useState, useEffect } from 'react';
import { Node } from 'reactflow';
import { X } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { DialplanNodeData } from '@/types/dialplan-nodes';

interface NodePropertyPanelProps {
  selectedNode: Node<DialplanNodeData> | null;
  onClose: () => void;
  onUpdate: (nodeId: string, data: Partial<DialplanNodeData>) => void;
}

export const NodePropertyPanel: React.FC<NodePropertyPanelProps> = ({
  selectedNode,
  onClose,
  onUpdate
}) => {
  const [formData, setFormData] = useState<Partial<DialplanNodeData>>({});
  const [xmlOverride, setXmlOverride] = useState(false);

  useEffect(() => {
    if (selectedNode) {
      setFormData(selectedNode.data || {});
      setXmlOverride(!!selectedNode.data?.xmlContent);
    }
  }, [selectedNode]);

  if (!selectedNode) return null;

  const handleSave = () => {
    onUpdate(selectedNode.id, formData);
    onClose();
  };

  const updateFormData = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateDialplanConfig = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      dialplanConfig: {
        ...(prev.dialplanConfig || {}),
        [field]: value
      }
    }));
  };

  return (
    <div className="w-96 bg-white border-l border-gray-200 p-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Node Properties</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Tabs defaultValue="metadata" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="metadata">Metadata</TabsTrigger>
          <TabsTrigger value="config">Config</TabsTrigger>
          <TabsTrigger value="xml">XML</TabsTrigger>
        </TabsList>

        {/* Tab 1: Metadata */}
        <TabsContent value="metadata" className="space-y-4">
          <div>
            <Label htmlFor="label">Name *</Label>
            <Input
              id="label"
              value={formData.label || ''}
              onChange={(e) => updateFormData('label', e.target.value)}
              placeholder="Node name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => updateFormData('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="priority">Priority</Label>
            <Input
              id="priority"
              type="number"
              value={formData.priority || 10}
              onChange={(e) => updateFormData('priority', parseInt(e.target.value))}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="enabled">Enabled</Label>
            <Switch
              id="enabled"
              checked={formData.enabled !== false}
              onCheckedChange={(checked) => updateFormData('enabled', checked)}
            />
          </div>
        </TabsContent>

        {/* Tab 2: Dialplan Config */}
        <TabsContent value="config" className="space-y-4">
          {selectedNode.type === 'tenant_context' && (
            <>
              <div>
                <Label htmlFor="context">Context Name</Label>
                <Input
                  id="context"
                  value={formData.dialplanConfig?.context || ''}
                  onChange={(e) => updateDialplanConfig('context', e.target.value)}
                  placeholder="e.g., demo.easyflowai.it"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-generated from tenant domain
                </p>
              </div>
            </>
          )}

          {(selectedNode.type === 'inbound_route' || selectedNode.type === 'outbound_route' || selectedNode.type === 'emergency_route') && (
            <>
              <div>
                <Label htmlFor="pattern">Pattern</Label>
                <Input
                  id="pattern"
                  value={formData.dialplanConfig?.pattern || ''}
                  onChange={(e) => updateDialplanConfig('pattern', e.target.value)}
                  placeholder="e.g., ^\\d{10}$"
                />
              </div>

              <div>
                <Label htmlFor="destination">Destination</Label>
                <Input
                  id="destination"
                  value={formData.dialplanConfig?.destination || ''}
                  onChange={(e) => updateDialplanConfig('destination', e.target.value)}
                  placeholder="Destination context or extension"
                />
              </div>

              {selectedNode.type === 'outbound_route' && (
                <>
                  <div>
                    <Label htmlFor="prefix">Prefix</Label>
                    <Input
                      id="prefix"
                      value={formData.dialplanConfig?.prefix || ''}
                      onChange={(e) => updateDialplanConfig('prefix', e.target.value)}
                      placeholder="e.g., 9"
                    />
                  </div>

                  <div>
                    <Label htmlFor="stripDigits">Strip Digits</Label>
                    <Input
                      id="stripDigits"
                      type="number"
                      value={formData.dialplanConfig?.stripDigits || 0}
                      onChange={(e) => updateDialplanConfig('stripDigits', parseInt(e.target.value))}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {(selectedNode.type === 'extension' || selectedNode.type === 'extension_group') && (
            <>
              {selectedNode.type === 'extension' && (
                <>
                  <div>
                    <Label htmlFor="extensionNumber">Extension Number</Label>
                    <Input
                      id="extensionNumber"
                      value={formData.dialplanConfig?.extensionNumber || ''}
                      onChange={(e) => updateDialplanConfig('extensionNumber', e.target.value)}
                      placeholder="e.g., 1000"
                    />
                  </div>

                  <div>
                    <Label htmlFor="callerId">Caller ID</Label>
                    <Input
                      id="callerId"
                      value={formData.dialplanConfig?.callerId || ''}
                      onChange={(e) => updateDialplanConfig('callerId', e.target.value)}
                      placeholder="Display name"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="destination">Destination</Label>
                <Input
                  id="destination"
                  value={formData.dialplanConfig?.destination || ''}
                  onChange={(e) => updateDialplanConfig('destination', e.target.value)}
                  placeholder={selectedNode.type === 'extension_group' ? 'ring_all' : 'Extension destination'}
                />
              </div>
            </>
          )}

          {selectedNode.type === 'time_condition' && (
            <div>
              <Label>Time Ranges</Label>
              <p className="text-sm text-gray-500 mt-1">
                Configure time ranges in the XML editor for advanced control.
              </p>
            </div>
          )}

          {selectedNode.type === 'ivr_menu' && (
            <div>
              <Label>Menu Options</Label>
              <p className="text-sm text-gray-500 mt-1">
                Configure IVR menu options in the XML editor.
              </p>
            </div>
          )}

          {selectedNode.type === 'voicemail_box' && (
            <div>
              <Label htmlFor="destination">Voicemail Box</Label>
              <Input
                id="destination"
                value={formData.dialplanConfig?.destination || ''}
                onChange={(e) => updateDialplanConfig('destination', e.target.value)}
                placeholder="Voicemail box number"
              />
            </div>
          )}
        </TabsContent>

        {/* Tab 3: XML Editor */}
        <TabsContent value="xml" className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="xmlOverride">Manual XML Override</Label>
            <Switch
              id="xmlOverride"
              checked={xmlOverride}
              onCheckedChange={setXmlOverride}
            />
          </div>

          {xmlOverride ? (
            <div>
              <Label htmlFor="xmlContent">Custom XML</Label>
              <Textarea
                id="xmlContent"
                value={formData.xmlContent || ''}
                onChange={(e) => updateFormData('xmlContent', e.target.value)}
                placeholder="Enter custom FreeSWITCH XML..."
                rows={15}
                className="font-mono text-sm"
              />
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ Manual XML overrides auto-generated configuration
              </p>
            </div>
          ) : (
            <div>
              <Label>Auto-Generated XML (Read-Only)</Label>
              <Textarea
                value="<!-- XML will be auto-generated from node properties -->"
                readOnly
                rows={15}
                className="font-mono text-sm bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enable "Manual XML Override" to edit directly
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex gap-2 mt-6">
        <Button onClick={handleSave} className="flex-1">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outline">
          Cancel
        </Button>
      </div>
    </div>
  );
};

